import { useEffect, useState } from "react";
import { Users, Plus, Trash2, Loader2, Copy } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { FullSpinner, Empty } from "@/components/States";
import { toast } from "sonner";

const RELATIONS = ["Father", "Mother", "Brother", "Sister", "Guardian"];
const PERMS = [["view_matches", "View selected matches"], ["shortlist", "Shortlist"], ["suggest", "Suggest profiles"], ["notes", "Add family notes"], ["view_chats", "View private chats"]];

export default function Family() {
  const [members, setMembers] = useState(null);
  const [form, setForm] = useState({ name: "", relation: "Father", contact: "", permissions: { view_matches: true, shortlist: true, suggest: true, notes: true, view_chats: false } });
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/family").then(({ data }) => setMembers(data.members));
  useEffect(() => { load(); }, []);
  if (!members) return <FullSpinner />;

  const invite = async () => {
    if (!form.name) return toast.error("Enter a name");
    setBusy(true);
    try { await api.post("/family/invite", form); toast.success("Family member invited"); setShowForm(false); setForm({ ...form, name: "", contact: "" }); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const remove = async (id) => { await api.delete(`/family/${id}`); load(); };
  const togglePerm = async (m, key) => { const np = { ...m.permissions, [key]: !m.permissions[key] }; await api.put(`/family/${m.id}`, { permissions: np }); load(); };

  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="family-page">
      <div className="flex items-center justify-between">
        <div><h1 className="font-serif text-3xl font-bold text-[#58111A] flex items-center gap-2"><Users className="w-6 h-6 text-[#C59B27]" /> Family Connect</h1><p className="text-sm text-[#605255]">Invite family with controlled access. Private chats are never shared unless you allow it.</p></div>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary text-sm" data-testid="invite-family"><Plus className="w-4 h-4" /> Invite</button>
      </div>

      {showForm && (
        <div className="mj-card p-5 space-y-3 animate-fadeup">
          <div className="grid sm:grid-cols-3 gap-3">
            <input className="mj-input" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="family-name" />
            <select className="mj-input" value={form.relation} onChange={(e) => setForm({ ...form, relation: e.target.value })}>{RELATIONS.map((r) => <option key={r}>{r}</option>)}</select>
            <input className="mj-input" placeholder="Email / Mobile" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} />
          </div>
          <div className="flex flex-wrap gap-2">
            {PERMS.map(([k, l]) => (
              <button key={k} onClick={() => setForm({ ...form, permissions: { ...form.permissions, [k]: !form.permissions[k] } })} className={`chip ${form.permissions[k] ? "!bg-[#781826] !text-white !border-[#781826]" : ""}`}>{l}</button>
            ))}
          </div>
          <button onClick={invite} disabled={busy} className="btn-primary w-full">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send Invite"}</button>
        </div>
      )}

      {members.length === 0 ? (
        <Empty icon={Users} title="No family members yet" message="Invite a parent or sibling to help in your search — you control exactly what they can see." />
      ) : members.map((m) => (
        <div key={m.id} className="mj-card p-5" data-testid={`family-${m.id}`}>
          <div className="flex items-center justify-between">
            <div><div className="font-serif text-lg font-semibold text-[#261A1C]">{m.name}</div><div className="text-xs text-[#605255]">{m.relation} · {m.status}</div></div>
            <button onClick={() => remove(m.id)} className="text-[#BE123C] hover:bg-red-50 p-2 rounded-lg"><Trash2 className="w-4 h-4" /></button>
          </div>
          <div className="mt-2 flex items-center gap-2 text-xs text-[#9E7B1B]"><span>Invite code:</span><code className="bg-[#FAF3E0] px-2 py-0.5 rounded">{m.invite_code}</code>
            <button onClick={() => { navigator.clipboard.writeText(m.invite_code); toast.success("Copied"); }} className="hover:text-[#781826]"><Copy className="w-3.5 h-3.5" /></button></div>
          <div className="mt-3 flex flex-wrap gap-2">
            {PERMS.map(([k, l]) => (
              <button key={k} onClick={() => togglePerm(m, k)} className={`chip ${m.permissions?.[k] ? "!bg-[#781826] !text-white !border-[#781826]" : ""}`}>{l}</button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
